import { CheckCircle2, Circle, Loader2, XCircle } from 'lucide-react';
import type { PipelineStatus } from '@/services/data-integration-api';

type StageColor = 'gray' | 'green' | 'orange' | 'red';

interface StageInfo {
  label: string;
  color: StageColor;
  time?: string | null;
}

function formatTime(ts: number | null | undefined): string | null {
  if (!ts) return null;
  const d = new Date(ts);
  return `${d.getMonth() + 1}月${d.getDate()}日`;
}

function getStages(p: PipelineStatus): StageInfo[] {
  // 映射 (Mapping)
  let mappingColor: StageColor = 'gray';
  if (p.creation_status === 'failed') mappingColor = 'red';
  else if (p.has_normalized_table && p.creation_status === 'created') mappingColor = 'green';

  // 同步 (Sync)
  let syncColor: StageColor = 'gray';
  if (p.last_sync_status === 'completed') syncColor = 'green';
  else if (p.last_sync_status === 'running') syncColor = 'orange';
  else if (p.last_sync_status === 'failed') syncColor = 'red';

  // 写入 (Write)
  let writeColor: StageColor = 'gray';
  if (p.has_prod_mapping && p.last_write_time) writeColor = 'green';
  else if (p.has_prod_mapping && !p.last_write_time) writeColor = 'orange';

  return [
    { label: '映射', color: mappingColor },
    { label: '同步', color: syncColor, time: formatTime(p.last_sync_time) },
    { label: '写入', color: writeColor, time: formatTime(p.last_write_time) },
  ];
}

const colorClasses: Record<StageColor, string> = {
  gray: 'text-muted-foreground',
  green: 'text-[var(--color-success,#16a34a)]',
  orange: 'text-[var(--color-warning,#f59e0b)]',
  red: 'text-destructive',
};

function StageIcon({ color }: { color: StageColor }) {
  const cls = `size-3.5 ${colorClasses[color]}`;
  switch (color) {
    case 'green':
      return <CheckCircle2 className={cls} />;
    case 'orange':
      return <Loader2 className={cls} />;
    case 'red':
      return <XCircle className={cls} />;
    default:
      return <Circle className={cls} />;
  }
}

interface PipelineStatusBadgeProps {
  pipeline?: PipelineStatus | null;
}

export default function PipelineStatusBadge({ pipeline }: PipelineStatusBadgeProps) {
  if (!pipeline) {
    return <span className="text-muted-foreground text-xs">-</span>;
  }

  const stages = getStages(pipeline);

  // Build tooltip text
  const tooltip = stages
    .map((s) => {
      let text = `${s.label}: ${s.color === 'green' ? '完成' : s.color === 'orange' ? '进行中' : s.color === 'red' ? '失败' : '未开始'}`;
      if (s.time) text += ` (${s.time})`;
      return text;
    })
    .join(' / ');

  return (
    <div className="inline-flex items-center gap-1" title={tooltip}>
      {stages.map((stage, i) => (
        <span key={stage.label} className="inline-flex items-center gap-0.5">
          <StageIcon color={stage.color} />
          {i < stages.length - 1 && (
            <span className="text-muted-foreground text-[10px] mx-0.5">›</span>
          )}
        </span>
      ))}
    </div>
  );
}
